import { ArrowDownLeft, ArrowLeft, ArrowUpRight, BadgeCheck, CheckCircle2, ChevronLeft, ChevronRight, Clock, Plus, RefreshCw, Wallet, X, XCircle } from 'lucide-react'
import React, { useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import AddWalletMoneyModal from './AddWalletMoneyModal'
import styles from './StoreWallet.module.css'
import '../../Components/Communications/Campaigns/shared/campaignConsole.css'

const DEFAULT_LIMIT = 20
const clampLimit = (l) => Math.min(50, Math.max(1, Number(l) || DEFAULT_LIMIT))

const fmtAmount = (v) => `₹${Math.abs(Number(v) || 0).toLocaleString('en-IN')}`

const fmtDate = (v) => {
    if (!v) return '—'
    const d = new Date(String(v).includes('T') ? v : String(v).replace(' ', 'T'))
    if (isNaN(d)) return v
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}
const fmtTime = (v) => {
    if (!v) return ''
    const d = new Date(String(v).includes('T') ? v : String(v).replace(' ', 'T'))
    if (isNaN(d)) return ''
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })
}

const isCredit = (t) => String(t?.transactionType).toUpperCase() === 'CREDIT'

// creditSource/debitedFor are free-form reason codes (e.g. "WHATSAPP_BROADCAST")
// with no fixed enum on the backend, so render them as readable text rather than
// mapping every possible value by hand. Some rows carry an opaque reference ID
// (mixed-case alphanumeric, no separators) in that same column instead of a
// real code — humanizing those produces garbage like "Cidcb34f24185361d9e60",
// so only humanize values that actually look like an ALL_CAPS_WITH_UNDERSCORES code.
const looksLikeReasonCode = (v) => /^[A-Z][A-Z_]*$/.test(String(v))
const humanizeCode = (v) => String(v).toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())

const StoreWallet = () => {
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = (() => {
        try { return (asCtx?.activeStore && asCtx.activeStore.trim() !== "") ? JSON.parse(asCtx.activeStore) : null }
        catch { return null }
    })()
    const navigate = useNavigate()
    const { paymentStatus, txnId } = useParams()

    const [summary, setSummary] = useState({ currentBalance: 0, availableBalance: 0, heldAmount: 0 })
    const [transactions, setTransactions] = useState([])
    const [openHolds, setOpenHolds] = useState([])
    const [page, setPage] = useState(1)
    const [limit, setLimit] = useState(DEFAULT_LIMIT)
    const [total, setTotal] = useState(0)
    const [loading, setLoading] = useState(true)
    const [showAddMoney, setShowAddMoney] = useState(false)
    const [paymentBanner, setPaymentBanner] = useState(paymentStatus ? { status: paymentStatus, txnId } : null)

    const getWalletData = async (p = page, l = limit) => {
        setLoading(true)
        try {
            const res = await fetch(BASE_URL_API + 'getStoreWalletTransactions', {
                method: 'POST',
                body: JSON.stringify({ token: authCtx.token, storeIdentifier: activeStore?.storeIdentifier, page: p, limit: clampLimit(l) })
            })
            if (!res.ok) return
            const data = await res.json()
            if (data?.status === 'success') {
                setSummary(data?.response?.summary ?? { currentBalance: 0, availableBalance: 0, heldAmount: 0 })
                setTransactions(data?.response?.transactions ?? [])
                setOpenHolds(data?.response?.openHolds ?? [])
                setTotal(data?.response?.pagination?.total ?? 0)
            } else if (data?.message === 'tokenExpired') {
                authCtx.logout()
            }
        } catch { /* silent */ } finally { setLoading(false) }
    }

    useEffect(() => { getWalletData(1, limit) }, [activeStore?.storeIdentifier])

    // Strip paymentStatus/txnId from the URL right after reading them so a
    // refresh doesn't re-show the banner, while keeping it visible via state.
    useEffect(() => {
        if (paymentStatus) navigate('/communications/store-wallet', { replace: true })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const totalPages = Math.max(1, Math.ceil(total / limit))
    const pageNumbers = () => {
        const pages = []
        for (let i = Math.max(1, page - 1); i <= Math.min(totalPages, page + 1); i++) pages.push(i)
        return pages
    }

    const changePage = (p) => {
        if (p < 1 || p > totalPages) return
        setPage(p)
        getWalletData(p, limit)
        document.getElementById('wallet-tx-table')?.scrollIntoView({ behavior: 'smooth' })
    }

    const changeLimit = (e) => {
        const l = clampLimit(e.target.value)
        setLimit(l)
        setPage(1)
        getWalletData(1, l)
    }

    const noTx = !loading && transactions.length === 0

    return (
        <div className={`${styles.wrapper} ccScope`}>
            {/* ── Page Header ── */}
            <div className={styles.pageHeader}>
                <div className={styles.headerLeft}>
                    <button className={styles.backBtn} onClick={() => navigate('/communications/all-campaigns')} type="button">
                        <ArrowLeft size={14} /> Campaigns
                    </button>
                    <div className={styles.titleRow}>
                        <h1 className={styles.pageTitle}>Store wallet</h1>
                        {!loading && <span className={styles.totalCount}>{total} transaction{total !== 1 ? 's' : ''}</span>}
                    </div>
                </div>
                <div className={styles.headerActions}>
                    <button className={styles.refreshBtn} onClick={() => getWalletData(page, limit)} type="button">
                        <RefreshCw size={13} /> Refresh
                    </button>
                    <button className={styles.addMoneyBtn} onClick={() => setShowAddMoney(true)} type="button">
                        <Plus size={13} /> Add money
                    </button>
                </div>
            </div>

            {/* ── Payment status banner ── */}
            {paymentBanner && (
                <div className={`${styles.paymentBanner} ${paymentBanner.status === 'success' ? styles.paymentBannerSuccess : styles.paymentBannerFailed}`}>
                    {paymentBanner.status === 'success' ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
                    <span className={styles.paymentBannerText}>
                        {paymentBanner.status === 'success'
                            ? 'Payment successful — your wallet has been updated.'
                            : 'Payment failed — no amount was added. Please try again.'}
                        {paymentBanner.txnId && <span className={styles.paymentBannerRef}> Ref: {paymentBanner.txnId}</span>}
                    </span>
                    <button className={styles.paymentBannerClose} onClick={() => setPaymentBanner(null)} type="button">
                        <X size={14} />
                    </button>
                </div>
            )}

            <AddWalletMoneyModal isOpen={showAddMoney} onClose={() => setShowAddMoney(false)} currentBalance={summary.currentBalance} />

            {/* ── Summary ── */}
            <p className={styles.statCaption}>Available balance = Current balance − On hold (reserved for campaigns in progress).</p>
            <div className={styles.statRow}>
                <div className="ccStatTile">
                    <div className="ccStatTop">
                        <span className="ccStatIcon" style={{ background: '#ede9fe', color: '#6d28d9' }}><Wallet size={12} /></span>
                        <span className="ccStatLabel">Current balance</span>
                    </div>
                    <div className="ccStatValue" style={{ color: '#6d28d9' }}>{fmtAmount(summary.currentBalance)}</div>
                    <div className="ccStatDelta">Total wallet funds, including money held for running campaigns.</div>
                </div>
                <div className="ccStatTile">
                    <div className="ccStatTop">
                        <span className="ccStatIcon" style={{ background: '#dcfce7', color: '#16a34a' }}><BadgeCheck size={12} /></span>
                        <span className="ccStatLabel">Available balance</span>
                    </div>
                    <div className="ccStatValue" style={{ color: '#16a34a' }}>{fmtAmount(summary.availableBalance)}</div>
                    <div className="ccStatDelta">What you can spend launching a new campaign right now.</div>
                </div>
                <div className="ccStatTile">
                    <div className="ccStatTop">
                        <span className="ccStatIcon" style={{ background: '#fef3c7', color: '#b45309' }}><Clock size={12} /></span>
                        <span className="ccStatLabel">On hold</span>
                    </div>
                    <div className="ccStatValue" style={{ color: summary.heldAmount > 0 ? '#b45309' : undefined }}>{fmtAmount(summary.heldAmount)}</div>
                    <div className="ccStatDelta">Reserved for campaigns that are currently sending.</div>
                </div>
            </div>

            {/* ── Open Holds ── */}
            {openHolds.length > 0 && (
                <div className={styles.tableCard}>
                    <div className={styles.sectionHeader}>
                        <Clock size={13} />
                        <span>Open holds</span>
                        <span className={styles.sectionCount}>{openHolds.length}</span>
                    </div>
                    <div className={styles.holdsScroll}>
                        <div className={styles.holdsHeader}>
                            <span>Campaign</span>
                            <span>Amount</span>
                            <span>Status</span>
                        </div>
                        {openHolds.map((h, i) => (
                            <div key={h.campaignIdentifier ?? i} className={styles.holdsRow}>
                                <div className={styles.descCell}>
                                    <span className={`${styles.txnIcon} ${styles.txnIconHeld}`}><Clock size={13} /></span>
                                    <div className={styles.descText}>
                                        <span className={styles.cellMain}>{h.campaignIdentifier || '—'}</span>
                                        <span className={styles.serviceCell}>{h.serviceType || '—'}</span>
                                    </div>
                                </div>
                                <span className={styles.amountHeld}>-{fmtAmount(h.amount)}</span>
                                <div className={styles.statusCell}>
                                    <span className={`${styles.chip} ${styles[h.isExpired ? 'chipBad' : 'chipWarn']}`}>
                                        {h.isExpired ? 'Expired' : 'Held'}
                                    </span>
                                    <span className={styles.cellSub}>
                                        {h.isExpired ? `Expired ${fmtDate(h.expiresAt)}` : `Expires ${fmtDate(h.expiresAt)}`}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ── Transactions ── */}
            <div className={styles.tableCard} id="wallet-tx-table">
                <div className={styles.tableTop}>
                    <span className={styles.sectionTitle}>Transactions</span>
                    <select className={styles.limitSelect} value={limit} onChange={changeLimit}>
                        <option value={10}>10 rows</option>
                        <option value={20}>20 rows</option>
                        <option value={50}>50 rows</option>
                    </select>
                </div>
                <div className={styles.tableScroll}>
                    <div className={styles.tableHeader}>
                        <span>Date</span>
                        <span>Description</span>
                        <span>Amount</span>
                        <span>Balance</span>
                    </div>

                    {loading ? (
                        Array.from({ length: 6 }).map((_, i) => (
                            <div key={i} className={styles.tableRowSkeleton}>
                                <div><div className={styles.skeletonBar} style={{ width: '70%', height: 13 }} /><div className={styles.skeletonBar} style={{ width: '40%', height: 10 }} /></div>
                                <div><div className={styles.skeletonBar} style={{ width: '60%', height: 13 }} /><div className={styles.skeletonBar} style={{ width: '35%', height: 10 }} /></div>
                                <div className={styles.skeletonBar} style={{ width: 50, height: 13 }} />
                                <div className={styles.skeletonBar} style={{ width: 56, height: 13 }} />
                            </div>
                        ))
                    ) : noTx ? (
                        <div className={styles.emptyState}>
                            <p className={styles.emptyTitle}>No transactions yet</p>
                            <p className={styles.emptySub}>Wallet top-ups and campaign spends will show up here.</p>
                        </div>
                    ) : (
                        transactions.map((t, i) => {
                            const credit = isCredit(t)
                            const reasonCode = credit ? t?.creditSource : t?.debitedFor
                            const desc = reasonCode && looksLikeReasonCode(reasonCode) ? humanizeCode(reasonCode) : (credit ? 'Wallet credit' : 'Wallet debit')
                            return (
                                <div key={t.ID ?? i} className={styles.tableRow}>
                                    <div className={styles.dateCell}>
                                        <span className={styles.datePrimary}>{fmtDate(t.transactionDoneOn)}</span>
                                        <span className={styles.dateSub}>{fmtTime(t.transactionDoneOn)}</span>
                                    </div>
                                    <div className={styles.descCell}>
                                        <span className={`${styles.txnIcon} ${credit ? styles.txnIconCredit : styles.txnIconDebit}`}>
                                            {credit ? <ArrowDownLeft size={13} /> : <ArrowUpRight size={13} />}
                                        </span>
                                        <div className={styles.descText}>
                                            <span className={styles.cellMain}>{desc}</span>
                                            {t.txnRefId && <span className={styles.cellSub}>{t.txnRefId}</span>}
                                        </div>
                                    </div>
                                    <span className={credit ? styles.amountCredit : styles.amountDebit}>
                                        {credit ? '+' : '-'}{fmtAmount(t.amount)}
                                    </span>
                                    <span className={styles.balanceCell}>{fmtAmount(t.currentBalance)}</span>
                                </div>
                            )
                        })
                    )}
                </div>
            </div>

            {/* ── Pagination ── */}
            {!loading && totalPages > 1 && (
                <div className={styles.pagination}>
                    <button className={styles.pageBtn} onClick={() => changePage(page - 1)} disabled={page === 1} type="button">
                        <ChevronLeft size={14} /> Previous
                    </button>
                    {pageNumbers().map(p => (
                        <button key={p} className={`${styles.pageNumBtn} ${p === page ? styles.pageNumBtnActive : ''}`} onClick={() => changePage(p)} type="button">
                            {p}
                        </button>
                    ))}
                    <button className={styles.pageBtn} onClick={() => changePage(page + 1)} disabled={page === totalPages} type="button">
                        Next <ChevronRight size={14} />
                    </button>
                </div>
            )}
        </div>
    )
}

export default StoreWallet
